<template>
  <!--
    TechnicalSheet
    -------------------------------------------------------------------------
    PRANCHA profissional de desenho técnico (uma por produto), no padrão de
    detalhamento de marmoraria: moldura, número do detalhe, escala declarada,
    desenho em planta, legenda de acabamentos com amostra de traço, notas e
    TITLE BLOCK (carimbo) no rodapé.

    IMPORTANTE: este componente é copiado via innerHTML para a janela de
    impressão — CSS scoped NÃO viaja. Todas as classes .ts-* moram em
    SHEET_PRINT_CSS (engine/print/stdCanvasStyles.js), injetado tanto pela
    janela de impressão quanto por installStdStyles (via DrawingCanvas).
  -->
  <div class="tech-sheet">
    <div class="ts-frame">
      <!-- cabeçalho: número do detalhe + título + escala -->
      <div class="ts-titlerow">
        <div class="ts-detail-number">{{ sheetNumber }}</div>
        <div class="ts-titles">
          <span class="ts-title">DET. {{ title }}</span>
          <span class="ts-scale">ESCALA 1:{{ sheetView.scaleDenominator }}</span>
        </div>
        <div class="ts-material">
          <span>Material: <strong>{{ product.nome }}</strong></span>
          <span v-if="product.comodo">Cômodo: <strong>{{ product.comodo }}</strong></span>
        </div>
      </div>

      <!-- vista em perspectiva (isométrica), como a prancha de referência -->
      <div v-if="stonePieces.length" class="ts-drawing ts-iso">
        <!-- plan dims = mesmas do DrawingCanvas abaixo: a perspectiva herda
             o layout da montagem (posições + rotação das peças) -->
        <IsometricView
          :pieces="pieces"
          :elements="extras.elements"
          :width="760"
          :height="250"
          :plan-width="760"
          :plan-height="480"
        />
      </div>

      <!-- área do desenho (planta) na escala nominal -->
      <div class="ts-drawing">
        <DrawingCanvas
          :pieces="pieces"
          :elements="extras.elements"
          :annotations="extras.annotations"
          :images="extras.images"
          :width="760"
          :height="480"
          :zoom="sheetView.zoom"
          :pan-x="sheetView.pan.x"
          :pan-y="sheetView.pan.y"
          :show-grid="false"
          :show-measurements="true"
          :technical-style="true"
          :readonly="true"
        />
      </div>

      <!-- rodapé: tabela de peças | legenda | notas -->
      <div class="ts-footer">
        <table class="ts-table">
          <thead>
            <tr>
              <th>Qtd</th>
              <th>Peça</th>
              <th>Medida</th>
              <th>Un.</th>
              <th class="ts-table-price">Preço</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(p, pi) in stonePieces" :key="`ts-row-${pi}`">
              <td>{{ p.quantity }}</td>
              <td>{{ p.description }}</td>
              <td>{{ formatMeasure(p.width) }}m × {{ formatMeasure(p.height) }}m · {{ Math.round(p.slabThicknessMm || 10) }}mm</td>
              <td>{{ p.billingUnit === "M2" ? "M²" : p.billingUnit }}</td>
              <td class="ts-table-price">{{ $helper.formataSaldo(p.total) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="4" class="ts-table-total-label">Total</td>
              <td class="ts-table-price ts-table-total-value">{{ $helper.formataSaldo(piecesTotal) }}</td>
            </tr>
          </tfoot>
        </table>

        <!-- (as legendas de acabamento saíram da prancha: os acabamentos já
             aparecem na listagem padrão do orçamento, montada no caixa) -->
        <div class="ts-notes">
          <div class="ts-block-title">NOTAS</div>
          <div class="ts-note ts-alert">1. CONFERIR TODAS AS MEDIDAS NO LOCAL ANTES DA EXECUÇÃO.</div>
          <div class="ts-note">2. Medidas em metros, salvo indicação em cm.</div>
          <div
            v-for="(ann, ai) in extras.annotations"
            :key="`ts-note-${ai}`"
            class="ts-note"
          >{{ ai + 3 }}. {{ ann.text }}</div>
        </div>
      </div>

      <!-- TITLE BLOCK (carimbo) -->
      <div class="ts-titleblock">
        <div class="ts-tb-cell ts-tb-brand">
          <img v-if="tenant.profileImage" :src="tenant.profileImage" class="ts-tb-logo" alt="" />
          <div>
            <div class="ts-tb-strong">{{ tenant.nome }}</div>
            <div v-if="tenant.cnpj" class="ts-tb-small">CNPJ: {{ tenant.cnpj }}</div>
          </div>
        </div>
        <div class="ts-tb-cell">
          <div class="ts-tb-label">CLIENTE</div>
          <div class="ts-tb-strong">{{ clientName }}</div>
          <div v-if="obraName" class="ts-tb-small">Obra: {{ obraName }}</div>
        </div>
        <div class="ts-tb-cell">
          <div class="ts-tb-label">DETALHE MARMORARIA</div>
          <div class="ts-tb-strong">{{ title }}</div>
          <div class="ts-tb-small">{{ sale.tipo || "Venda" }} #{{ sale.id_transacao || "" }}</div>
        </div>
        <div class="ts-tb-cell">
          <div class="ts-tb-label">DATA</div>
          <div class="ts-tb-strong">{{ today }}</div>
          <div v-if="sellerName" class="ts-tb-small">Resp.: {{ sellerName }}</div>
        </div>
        <div class="ts-tb-cell ts-tb-last">
          <div class="ts-tb-label">ESCALA</div>
          <div class="ts-tb-strong">1:{{ sheetView.scaleDenominator }}</div>
          <div class="ts-tb-small">FOLHA {{ sheetNumber }}/{{ totalSheets }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DrawingCanvas from "./DrawingCanvas.vue";
import IsometricView from "./IsometricView.vue";
import { normalizeFichaTecnica } from "./engine/drawing/normalizeFichaTecnica";
import { formatMeasure } from "./engine/drawing/measurementUtils";
import {
  getRenderablePieces,
  getSheetView
} from "./engine/drawing/drawingFactory";

export default {
  name: "TechnicalSheet",
  components: { DrawingCanvas, IsometricView },
  methods: { formatMeasure },
  props: {
    product: { type: Object, required: true },
    sale: { type: Object, default: () => ({}) },
    tenant: { type: Object, default: () => ({}) },
    sheetNumber: { type: Number, default: 1 },
    totalSheets: { type: Number, default: 1 }
  },
  computed: {
    pieces() {
      return normalizeFichaTecnica(this.product.ficha_tecnica || []);
    },
    stonePieces() {
      return getRenderablePieces(this.pieces);
    },
    piecesTotal() {
      return this.stonePieces.reduce((sum, p) => sum + (parseFloat(p.total) || 0), 0);
    },
    extras() {
      const extras = this.product.drawingExtras || {};
      return {
        elements: Array.isArray(extras.elements) ? extras.elements : [],
        annotations: Array.isArray(extras.annotations) ? extras.annotations : [],
        images: Array.isArray(extras.images) ? extras.images : []
      };
    },
    sheetView() {
      return getSheetView(this.pieces, 760, 480);
    },
    title() {
      return this.product.descricao_personalizada || this.product.nome || "Peça";
    },
    clientName() {
      const compra = this.sale.compra || {};
      return (compra.cliente && compra.cliente.nome) || "—";
    },
    obraName() {
      const compra = this.sale.compra || {};
      return compra.obra && compra.obra.nome;
    },
    sellerName() {
      const caixa = this.sale.caixa || {};
      return caixa.funcionario && caixa.funcionario.nome;
    },
    today() {
      return this.$moment ? this.$moment().format("DD/MM/YYYY") : "";
    }
  }
};
</script>
